import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { resolveBranding } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { canvasAuthSynced } from '@/components/iframe/CanvasAuthBridge';
import { useCanvasIframe } from '../hooks/useCanvasIframe';
import { encodeCanvasBranding } from '../utils/canvas-branding';

const CANVAS_URL = import.meta.env.VITE_CANVAS_URL || 'https://canvas.optisigns.com';

/** Delay before background-preloading Canvas (ms). Lets main content load first. */
const PRELOAD_DELAY_MS = 8_000;

/**
 * Persistent Canvas iframe that lives in AppShell (outside <Outlet />).
 *
 * Preloads the Canvas app after a delay so main scio3 content loads first.
 * If the user navigates to /app/templateManagement before the delay expires,
 * the delay is cancelled and the iframe loads immediately.
 *
 * Hiding strategy: The iframe is always fully opaque and positioned absolute inset-0.
 * When NOT on Templates route: z-index: -1 puts it behind the current page content
 * (which has a solid background). Canvas sees itself as "visible" and renders normally.
 * When ON Templates route: z-index: 10 brings it to front. TemplatesPage returns null,
 * so Canvas is the only visible content.
 */
export function PersistentCanvasFrame() {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);
  const [isReady, setIsReady] = useState(false);
  const [authSynced, setAuthSynced] = useState(false);
  // Controls when the iframe actually renders. Starts false; set to true either
  // by the delay timer or immediately when user navigates to Templates.
  const [shouldLoad, setShouldLoad] = useState(false);
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track the previous pathname to detect leaving Templates route.
  const prevPathnameRef = useRef(location.pathname);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const isTemplatesRoute = location.pathname === '/app/templateManagement';

  // Wait for CanvasAuthBridge to sync auth before loading the Canvas iframe.
  useEffect(() => {
    canvasAuthSynced.then(() => setAuthSynced(true));
  }, []);

  // Deferred preload: start a timer after auth is synced. If the user navigates
  // to Templates before the timer fires, cancel it and load immediately.
  useEffect(() => {
    if (!authSynced || !me) return;
    if (shouldLoad) return; // already loading

    if (isTemplatesRoute) {
      // User is already on Templates — load immediately, cancel any pending timer
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
      setShouldLoad(true);
      return;
    }

    // Not on Templates — start background preload timer (only if not already started)
    if (!delayTimerRef.current) {
      delayTimerRef.current = setTimeout(() => {
        delayTimerRef.current = null;
        setShouldLoad(true);
      }, PRELOAD_DELAY_MS);
    }

    return () => {
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
    };
  }, [authSynced, me, isTemplatesRoute, shouldLoad]);

  const canvasOrigin = useMemo(() => {
    try {
      return new URL(CANVAS_URL).origin;
    } catch {
      return '';
    }
  }, []);

  const iframeSrc = useMemo(() => {
    if (!me || !authSynced || !shouldLoad) return '';

    const awsBucket = import.meta.env.VITE_AWS_BUCKET_NAME || 'optisigns-assets';
    const brandingData = resolveBranding(company?.brandingData ?? me?.brandingData, awsBucket);
    const encoded = encodeCanvasBranding(brandingData, i18n.language);
    const params = new URLSearchParams();
    params.set('branding', encoded);
    return `${CANVAS_URL}?${params.toString()}`;
  }, [company?.brandingData, me, i18n.language, authSynced, shouldLoad]);

  const onReady = useCallback(() => {
    setIsReady(true);
  }, []);

  useCanvasIframe({ canvasOrigin, onReady });

  // Reset Canvas to homepage when leaving Templates route (so it starts fresh on return).
  useEffect(() => {
    const prev = prevPathnameRef.current;
    prevPathnameRef.current = location.pathname;

    if (prev === '/app/templateManagement' && !isTemplatesRoute && iframeSrc) {
      if (iframeRef.current) {
        iframeRef.current.src = iframeSrc;
      }
    }
  }, [location.pathname, isTemplatesRoute, iframeSrc]);

  // Don't render until ready to load
  if (!iframeSrc) return null;

  return (
    <div
      className="absolute inset-0"
      style={{
        // Behind page content when hidden, in front when on Templates route.
        // No opacity/visibility tricks — Canvas stays fully visible to avoid
        // lazy rendering issues (IntersectionObserver, etc.).
        zIndex: isTemplatesRoute ? 10 : -1,
        pointerEvents: isTemplatesRoute ? 'auto' : 'none',
      }}
    >
      {isTemplatesRoute && !isReady && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/80">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        className="h-full w-full border-0"
        title={t('Templates')}
        allow="clipboard-write"
      />
    </div>
  );
}
